/**
 * Selectors
 */
export const DSP_ACTIVATION_REQUEST_PAGE_ID = 'dsp-activation-request';

/**
 * Translation keys
 */
export const SETTINGS_HEADER_KEY = 'settings_header';
export const BILLING_HEADER_KEY = 'billing_header';
export const MANAGER_ACCOUNT_LABEL_KEY = 'manager_account_label';
export const TIME_ZONE_LABEL_KEY = 'time_zone_label';
export const TIME_ZONE_TOOLTIP_KEY = 'time_zone_tooltip';
export const INDUSTRY_LABEL_KEY = 'industry_label';
export const PARTNER_ID_LABEL_KEY = 'partner_id_label';
export const OPTIONAL_LABEL_KEY = 'optional_label';
export const INVOICE_COUNTRY_LABEL_KEY = 'invoice_country_label';
export const INVOICE_COUNTRY_TOOLTIP_KEY = 'invoice_country_tooltip';
export const PO_NUMBER_LABEL_KEY = 'po_number_label';
export const PO_NUMBER_TOOLTIP_KEY = 'po_number_tooltip';
export const PO_CHECKBOX_LABEL_KEY = 'po_checkbox_label';
export const COMPANY_NAME_LABEL_KEY = 'company_name_label';
export const CONTACT_NAME_LABEL_KEY = 'contact_name_label';
export const PRIMARY_EMAIL_LABEL_KEY = 'primary_email_label';
export const PHONE_NUMBER_LABEL_KEY = 'phone_number_label';
export const WEBSITE_LABEL_KEY = 'website_label';
export const BUSINESS_REGISTRATION_NUMBER_LABEL_KEY =
  'business_registration_number_label';
export const ADDRESS_LINE_1_LABEL_KEY = 'address_line_1_label';
export const ADDRESS_LINE_2_LABEL_KEY = 'address_line_2_label';
export const POSTAL_CODE_LABEL_KEY = 'postal_code_label';
export const CITY_LABEL_KEY = 'city_label';
export const COUNTRY_LABEL_KEY = 'country_label';
export const STATE_LABEL_KEY = 'state_label';
export const SELECT_PLACEHOLDER_KEY = 'select_placeholder';
export const REQUIRED_ERROR_KEY = 'required_error';
export const SUBMIT_ERROR_KEY = 'submit_error';
export const GENERIC_ERROR_KEY = 'generic_error';
export const MAX_LENGTH_ERROR_50_KEY = 'max_length_error_50';
export const MAX_LENGTH_ERROR_60_KEY = 'max_length_error_60';
export const MAX_LENGTH_ERROR_100_KEY = 'max_length_error_100';
export const EMAIL_ERROR_KEY = 'email_error';
export const PHONE_ERROR_KEY = 'phone_error';
export const WEBSITE_ERROR_KEY = 'website_error';
export const VAT_ERROR_KEY = 'vat_error';
export const CNPJ_ERROR_KEY = 'cnpj_error';
export const RFC_ERROR_KEY = 'rfc_error';
export const PAN_ERROR_KEY = 'pan_error';
export const GST_ERROR_KEY = 'gst_error';
export const AGREEMENT_MODAL_HEADER_KEY = 'agreement_modal_header';
export const AGREEMENT_LANGUAGE_LABEL_KEY = 'agreement_language_label';
export const AGREEMENT_TITLE_KEY = 'agreement_title';
export const AGREEMENT_ERROR_HEADER_KEY = 'agreement_error_header';
export const AGREEMENT_ERROR_BODY_KEY = 'agreement_error_body';
export const AGREEMENT_CANCEL_KEY = 'agreement_cancel';
export const AGREEMENT_ACCEPT_KEY = 'agreement_accept';

/**
 * Tax field translation keys.
 *
 * Labels for country-conditional tax inputs rendered by TaxFields.tsx.
 * The set of fields shown per country mirrors what
 * D16GAdspActivationService's CountryTaxTranslator subclasses require.
 */
export const VAT_LABEL_KEY = 'vat_label';
export const GST_LABEL_KEY = 'gst_label';
export const CNPJ_LABEL_KEY = 'cnpj_label';
export const RFC_LABEL_KEY = 'rfc_label';
export const PAN_LABEL_KEY = 'pan_label';
export const CDFE_LABEL_KEY = 'cdfe_label';
export const CDFE_DEFAULT_HINT_KEY = 'cdfe_default_hint';
export const PEC_LABEL_KEY = 'pec_label';
export const KEP_LABEL_KEY = 'kep_label';
export const KEP_TOOLTIP_KEY = 'kep_tooltip';
export const MX_TAX_CODE_LABEL_KEY = 'mx_tax_code_label';

/**
 * MX TAX CODE allow-list. Must match D16GAdspActivationService
 * CountryTaxTranslator.taxCodeDescriptionMap exactly — when marketplace ==
 * MX, the backend rejects values not in this map. Display format is
 * "<code> (<description>)" matching legacy
 * D16GAdspActivation-UIComponents/billing/CountryStateAndTaxCode.tsx.
 */
export const MX_TAX_CODE_DESCRIPTIONS: Record<string, string> = {
  '601': 'General de Ley Personas Morales',
  '603': 'Personas Morales con Fines no Lucrativos',
  '605': 'Sueldos y Salarios e Ingresos Asimilados a Salarios',
  '606': 'Arrendamiento',
  '607': 'Régimen de Enajenación o Adquisición de Bienes',
  '608': 'Demás ingresos',
  '610': 'Residentes en el Extranjero sin Establecimiento Permanente en México',
  '611': 'Ingresos por Dividendos (socios y accionistas)',
  '612': 'Personas Físicas con Actividades Empresariales y Profesionales',
  '614': 'Ingresos por intereses',
  '615': 'Régimen de los ingresos por obtención de premios',
  '616': 'Sin obligaciones fiscales',
  '620':
    'Sociedades Cooperativas de Producción que optan por diferir sus ingresos',
  '621': 'Incorporación Fiscal',
  '622': 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras',
  '623': 'Opcional para Grupos de Sociedades',
  '624': 'Coordinados',
  '625':
    'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas',
  '626': 'Régimen Simplificado de Confianza',
};

export const MX_TAX_CODES: ReadonlyArray<string> = Object.keys(
  MX_TAX_CODE_DESCRIPTIONS,
);

/**
 * Default MX TAX CODE values when the marketplace is MX, mirroring legacy
 * D16GAdspActivation-UIComponents behaviour:
 * - billing country == MX: default to '601' (General de Ley Personas Morales)
 * - billing country != MX: default to '616' (Sin obligaciones fiscales)
 */
export const MX_TAX_CODE_DEFAULT_BILLING_MX = '601';
export const MX_TAX_CODE_DEFAULT_BILLING_NON_MX = '616';

/**
 * CDFE default value. Italian recipients without electronic invoicing send
 * the placeholder `0000000`. The backend requires a non-empty
 * cdfeTaxInformation.registrationNumber for IT, so when the user leaves the
 * input blank we substitute this default at submit time.
 */
export const CDFE_DEFAULT_VALUE = '0000000';

/**
 * Tax registration number format regexes. The authoritative source of truth
 * is D16GRodeoService's brazil-config, which is what actually rejects a
 * malformed tax id downstream (throwing "CNPJ tax id is invalid" etc.). These
 * front-end copies backstop that validation so malformed values are caught
 * client-side instead of failing downstream in RodeoService /
 * D16GAdspEntityReplication — see V2264441561 for the incident where a
 * 12-digit CNPJ passed through with no client-side format check and poisoned
 * cross-region replication. D16GAdspActivationService is being deprecated, so
 * mirroring Rodeo here is the durable guard.
 *
 * CNPJ/RFC/GST/PAN mirror Rodeo's `CountryToTaxCodeValidationRegex`
 * (D16GRodeoService.cfg L10399-L10428). Rodeo's regexes carry a mid-pattern
 * `^$` quirk (`^(...)^$`) which is a no-op anchor for single-line input, so we
 * use clean `^(...)$` here.
 *
 * No build-time or runtime link exists to Rodeo's brazil-config (it is not
 * exposed as a client-callable API — confirmed by D16GRodeoService's own
 * first-party UI, D16GRodeoWebsiteAdvertiserBillingModal, also hand-copying
 * these regexes rather than importing them, and its CNPJ copy having already
 * drifted from Rodeo's). Update this file if the referenced cfg lines change.
 */
export const CNPJ_VALIDATION_REGEX =
  /^([0-9]{14}|[0-9]{2}\.[0-9]{3}\.[0-9]{3}\/[0-9]{4}-[0-9]{2})$/;
// Rodeo RFC.MX (L10415) is [a-zA-Z0-9&]{12,13} — the `&` is intentional and
// matches Rodeo (company RFCs may contain it). Rodeo also carries a stricter
// structured `RFCValidationRegex` (L10382); we mirror the looser new-rules
// mapping, consistent with the legacy ActivationConsole UI.
export const RFC_VALIDATION_REGEX = /^[a-zA-Z0-9&]{12,13}$/;
export const PAN_VALIDATION_REGEX = /^[a-zA-Z0-9]{10}$/;
export const GST_VALIDATION_REGEX_BY_COUNTRY: Record<string, RegExp> = {
  IN: /^[a-zA-Z0-9]{15}$/,
  CA: /^.{0,18}$/,
  SG: /^.{0,18}$/,
};

/**
 * VAT validation, mirroring Rodeo's two-tier config:
 *  - `VAT_VALIDATION_REGEX_BY_COUNTRY` ← Rodeo `CountryToVatValidationRegex`
 *    (D16GRodeoService.cfg L10332-L10380), the always-on ~40-country map.
 *  - `VAT_TREATMENT_VALIDATION_REGEX_BY_COUNTRY` ← Rodeo
 *    `CountryToTaxCodeValidationRegex.VAT` (L10400-L10413), the "new rules"
 *    (isTaxIdValidationUsingNewRules) subset.
 *
 * A value is accepted when it matches EITHER map for the country (matching the
 * legacy UI's getVatNumberValidatorTreatment), when only one map has the
 * country that map is used, and when neither has it the value is accepted —
 * see `isVatValid`. Note BR/IN VAT are `.*` in Rodeo (VAT is not the BR/IN
 * tax mechanism), so any value passes there.
 */
export const VAT_VALIDATION_REGEX_BY_COUNTRY: Record<string, RegExp> = {
  AE: /^(1[0-9]{9}00003)$/,
  AL: /^([A-Z][0-9]{8}[A-Z])$/,
  AT: /^(ATU[0-9]{8}|[0-9]{9})$/,
  AU: /^([0-9]{11})$/,
  BE: /^BE[0-9]{10}$/,
  BG: /^BG[0-9]{9,10}$/,
  BR: /^.*$/,
  BY: /^([0-9]{9})$/,
  CH: /^(\d{6}|CHE(\d{9}|-\d{3}\.\d{3}\.\d{3}( ?))(MWST|TVA|IVA))$/,
  // Preserves Rodeo's `a-zA-z` (sic) character range for exact parity.
  CN: /^([0-9]{6}[0-9a-zA-z]{9}|[0-9a-zA-Z]{18})$/,
  CY: /^CY[0-9]{8}[A-Z]$/,
  CZ: /^CZ[0-9]{8,10}$/,
  DE: /^DE[0-9]{9}$/,
  DK: /^DK([0-9]{2} *){3}[0-9]{2}$/,
  EE: /^EE[0-9]{9}$/,
  ES: /^(ES|)([0-9]{8}[A-Z]|[A-Z][0-9]{7}[0-9A-Z])$/,
  FI: /^FI[0-9]{8}$/,
  FR: /^FR[A-Z0-9]{2} *[0-9]{9}$/,
  GB: /^GB([0-9]{3} *[0-9]{4} *[0-9]{2}( *[0-9]{3})?|(GD|HA)[0-9]{3})$/,
  GR: /^EL[0-9]{9}$/,
  HR: /^HR[0-9]{11}$/,
  HU: /^HU[0-9]{8}$/,
  IE: /^IE[0-9][0-9A-Z+*][0-9]{5}[A-Z]{1,2}$/,
  IN: /^.*$/,
  IS: /^[0-9]{6}$/,
  IT: /^IT[0-9]{11}$/,
  KR: /^[0-9]{3}-[0-9]{2}-[0-9]{5}$/,
  LI: /^[0-9]{5}$/,
  LT: /^LT([0-9]{9}|[0-9]{12})$/,
  LU: /^LU[0-9]{8}$/,
  LV: /^LV[0-9]{11}$/,
  MT: /^MT[0-9]{8}$/,
  MY: /^([0-9]{12})$/,
  NL: /^NL[0-9]{9}B[0-9]{2}$/,
  NZ: /^[0-9]{8,9}$/,
  PL: /^PL[0-9]{10}$/,
  PT: /^PT[0-9]{9}$/,
  RO: /^RO[0-9]{2,10}$/,
  RS: /^([0-9]{9})$/,
  RU: /^([0-9]{13}|[0-9]{15})$/,
  SA: /^(3[0-9]{9}[0-1]{1}0003)$/,
  SE: /^SE[0-9]{12}$/,
  SI: /^SI[0-9]{8}$/,
  SK: /^SK[0-9]{10}$/,
  TR: /^([0-9]{10})$/,
  TW: /^([0-9]{8})$/,
  ZA: /^[0-9]{10}$/,
};

export const VAT_TREATMENT_VALIDATION_REGEX_BY_COUNTRY: Record<string, RegExp> =
  {
    GB: /^GB[0-9]{9}$/,
    DE: /^DE[0-9]{9}$/,
    FR: /^FR[0-9]{11}$/,
    ES: /^ES[a-zA-Z0-9]{9}$/,
    IT: /^IT[0-9]{11}$/,
    SE: /^SE[0-9]{12}$/,
    NL: /^NL[0-9]{9}B[0-9]{2}$/,
    AE: /^[0-9]{15}$/,
    TR: /^[0-9]{10}$/,
    AU: /^[0-9]{11}$/,
    JP: /^[0-9]{12,13}$/,
    SA: /^[0-9]{15}$/,
  };

/**
 * Returns true when `value` is an acceptable VAT number for `country`,
 * mirroring Rodeo's dual-map behavior (accept if either the base or the
 * treatment regex matches; accept when the country has no configured regex).
 */
export const isVatValid = (country: string, value: string): boolean => {
  const base = VAT_VALIDATION_REGEX_BY_COUNTRY[country];
  const treatment = VAT_TREATMENT_VALIDATION_REGEX_BY_COUNTRY[country];
  if (base && treatment) return base.test(value) || treatment.test(value);
  if (base) return base.test(value);
  if (treatment) return treatment.test(value);
  return true;
};

export const VAT_NOT_APPLICABLE_COUNTRIES: ReadonlyArray<string> = ['MX', 'QA'];

export const VAT_REQUIRED_COUNTRIES: ReadonlyArray<string> = ['TR'];

export const optionalVatValidator =
  (billingCountry: string, errorMessage: string) =>
  (value: string): true | string =>
    !value || isVatValid(billingCountry, value) || errorMessage;

/**
 * Per-billing-country tax requirements. Source of truth is
 * D16GAdspActivationService's CountryTaxTranslator subclasses:
 *
 * - vat: optional for every translator that calls handleVatAndMXTaxCode.
 *   IndiaTranslator does not call it, so VAT is not shown for IN.
 * - gst: required for CA/SG (CanadaTranslator, SingaporeTranslator);
 *   optional for IN (IndiaTranslator setOptionalField).
 * - cnpj: required for BR.
 * - rfc: required for MX.
 * - pan: required for IN.
 * - cdfe / pec: required for IT.
 * - kep: optional for TR.
 *
 * MX TAX CODE is keyed off marketplace (handleMXTaxCode), not billing
 * country, and is handled separately in TaxFields.tsx.
 */
export interface CountryTaxRequirements {
  vat: 'none' | 'optional';
  gst: 'none' | 'optional' | 'required';
  cnpj: 'none' | 'required';
  rfc: 'none' | 'required';
  pan: 'none' | 'required';
  cdfe: 'none' | 'required';
  pec: 'none' | 'required';
  kep: 'none' | 'optional';
}

const REQUIREMENTS_OTHER: CountryTaxRequirements = {
  vat: 'optional',
  gst: 'none',
  cnpj: 'none',
  rfc: 'none',
  pan: 'none',
  cdfe: 'none',
  pec: 'none',
  kep: 'none',
};

export const TAX_REQUIREMENTS_BY_COUNTRY: Record<
  string,
  CountryTaxRequirements
> = {
  CA: { ...REQUIREMENTS_OTHER, gst: 'required' },
  SG: { ...REQUIREMENTS_OTHER, gst: 'required' },
  BR: { ...REQUIREMENTS_OTHER, cnpj: 'required' },
  MX: { ...REQUIREMENTS_OTHER, rfc: 'required' },
  IT: { ...REQUIREMENTS_OTHER, cdfe: 'required', pec: 'required' },
  IN: {
    vat: 'none',
    gst: 'optional',
    cnpj: 'none',
    rfc: 'none',
    pan: 'required',
    cdfe: 'none',
    pec: 'none',
    kep: 'none',
  },
  TR: { ...REQUIREMENTS_OTHER, kep: 'optional' },
};

export const getTaxRequirements = (
  billingCountry: string,
): CountryTaxRequirements =>
  TAX_REQUIREMENTS_BY_COUNTRY[billingCountry] ?? REQUIREMENTS_OTHER;
